import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@symphenia/ui-components';
import { usersApi } from '@/lib/users/users.api';
import { apiErrorText } from '@/lib/api-error';
import type { User } from '@/lib/users/user.types';

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * AP-38-F — **Einem Mitarbeiter ohne Zugang den Zugang geben** (Handlung, Knopf mit Verb).
 *
 * Die Einladung geht an seine Adresse. Arbeitet die Person mit dieser Adresse
 * schon für eine andere Firma, meldet sie sich mit ihrem Konto an und nimmt den
 * Zugang an — erst dann steht ihr Konto an dieser Mitgliedschaft.
 *
 * Scheitert nur die Mail (503), ist der Zugang trotzdem vorbereitet: Der Dialog
 * schließt, die Liste lädt neu und zeigt „Einladung erneut senden" — ein zweiter
 * Klick auf „Zugang geben" liefe sonst in „hat schon einen Zugang".
 */
export function ZugangGebenDialog({
  user,
  onClose,
  onSuccess,
}: {
  user: User | null;
  onClose: () => void;
  onSuccess: (text: string, art: 'ok' | 'err') => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setEmail(user?.email ?? '');
    setFehler(null);
  }, [user]);

  if (!user) return null;
  const name = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ') || user.email || '—';

  const geben = async (e: React.FormEvent) => {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      await usersApi.grantAccess(user.id, { email: email.trim() || null });
      onSuccess(t('users.zugangGeben.gesendet', { name }), 'ok');
      onClose();
    } catch (err) {
      if ((err as { response?: { status?: number } }).response?.status === 503) {
        onSuccess(apiErrorText(err, t('common.error')), 'err');
        onClose();
        return;
      }
      setFehler(apiErrorText(err, t('common.error')));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" hideCloseButton>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('users.zugangGeben.titel', { name })}</h2>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('common.cancel')}>
          ✕
        </button>
      </div>
      <form onSubmit={geben} className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('users.zugangGeben.hinweis')}</p>
        {fehler && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground" htmlFor="zg-email">{t('auth.email')} *</label>
          <input id="zg-email" type="email" required autoFocus className={eingabe} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-input px-4 py-2 text-sm text-foreground hover:bg-muted">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={laeuft || !email.trim()} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {laeuft ? t('common.loading') : t('users.zugangGeben.knopf')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
